import { useTranslation } from 'react-i18next';
import { Select } from '@shape-ds/components';
import useFilters, { FilterDefinitions } from 'hooks/useFilters/useFilters';
import { VibrationData, MaintenancePlanNotes } from 'queries/externalReferences/contract';
import { createDateOptions, createPreventiveOptions } from './filterOptions';

export type TableData = {
  [key: string]: number | string | null | VibrationData | MaintenancePlanNotes[];
};
type Props<T> = {
  onChangeFilters: (data: T[]) => void;
  data: T[];
  enableColumnFilter?: boolean;
  setPeriod: (period: string) => void;
  period: string;
  searchKey?: Array<string>;
  hasPreventiveFilter?: boolean;
};

const Filters = <T extends TableData>({
  onChangeFilters,
  data,
  enableColumnFilter,
  setPeriod,
  period,
  searchKey,
  hasPreventiveFilter = false,
}: Props<T>) => {
  const { t } = useTranslation();
  const columnFilterOptions = data
    .map((item) => {
      const keys = Object.keys(item);
      const tag = keys.find((key) => /[a-zA-Z]/.test(key)) ?? '';
      return { label: tag, value: tag };
    })
    .filter((item, index, self) => self.findIndex((t) => t.label === item.label) === index);

  const { onChange, allActiveFilters } = useFilters<T>({
    data,
    onChangeFilters,
    multipleFieldSearch: searchKey,
    defaultFilters: { columnFilter: [columnFilterOptions[0]?.value] } as Partial<
      FilterDefinitions<T>
    >,
  });

  return (
    <>
      {enableColumnFilter && (
        <Select
          data={columnFilterOptions}
          className="column-filter"
          onChange={(e, selectedItem) => {
            const selectedValue = (selectedItem as { props: { value: string } })?.props.value;
            onChange({ filterName: 'columnFilter', filterValue: [selectedValue] });
          }}
          value={String(allActiveFilters?.columnFilter)}
        />
      )}
      <div className="filters">
        <div>
          {hasPreventiveFilter && (
            <Select
              data={createPreventiveOptions(t)}
              onChange={(e, selectedItem) => {
                const selectedValue = (selectedItem as { props: { value: string } })?.props.value;
                onChange({ filterName: 'type', filterValue: [selectedValue] });
              }}
              value={allActiveFilters.type === undefined ? 'all' : String(allActiveFilters.type)}
            />
          )}
          <Select
            data={createDateOptions(t)}
            variant="outlined"
            onChange={(e, selectedItem) => {
              const selectedValue = (selectedItem as { props: { value: string } })?.props.value;
              setPeriod(selectedValue);
            }}
            placeholder={t(`monitoring:tables.filters.dateOptions.365`)}
            value={period}
          />
        </div>
      </div>
    </>
  );
};

export default Filters;
